import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Timer } from 'lucide-react';
import { CardV2, CardHeader, CardTitle, CardContent } from '../../../components/CardV2/CardV2';
import CommissionContractsStatisticsAPI, {
  type TimeToSellResponse,
} from '../../../api/CommissionContractsStatisticsAPI';

type TimeRange = { start: string; end: string };

function formatDays(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) {
    return '—';
  }
  return value.toFixed(1);
}

type TimeToSellCardProps = {
  timeRange: TimeRange;
};

function TimeToSellCard({ timeRange }: TimeToSellCardProps) {
  const { t } = useTranslation();
  const [data, setData] = useState<TimeToSellResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setIsLoading(true);

    CommissionContractsStatisticsAPI.getTimeToSell({
      startTime: timeRange.start,
      endTime: timeRange.end,
    })
      .then((res) => {
        if (!mounted) return;
        setData(res.data ?? null);
      })
      .catch((error) => {
        console.error('Failed to load commission contracts time to sell', {
          startTime: timeRange.start,
          endTime: timeRange.end,
          error,
        });
        if (!mounted) return;
        setData(null);
      })
      .finally(() => {
        if (mounted) setIsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [timeRange.start, timeRange.end]);

  const hasSample = (data?.sampleSize ?? 0) > 0;

  return (
    <CardV2>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Timer className="h-5 w-5" />
          {t('stats.commission_contracts.time_to_sell')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1">
          <p className="text-3xl font-bold tabular-nums text-foreground">
            {isLoading ? '...' : hasSample ? formatDays(data?.averageDays) : '—'}
          </p>
          <p className="text-sm text-muted-foreground">
            {t('stats.commission_contracts.time_to_sell_hint')}
          </p>
        </div>
        {!isLoading && hasSample && (
          <div className="space-y-1 text-xs text-muted-foreground">
            <p>
              {t('stats.commission_contracts.time_to_sell_median', {
                days: formatDays(data?.medianDays),
              })}
            </p>
            <p>
              {t('stats.commission_contracts.time_to_sell_sample', {
                count: data?.sampleSize ?? 0,
              })}
            </p>
          </div>
        )}
      </CardContent>
    </CardV2>
  );
}

export default TimeToSellCard;
